/**
 * Module Color Utilities — Single Source of Truth
 *
 * Part of the Content domain (DDD B3).
 * Moved from src/utils/moduleColors.ts.
 *
 * Extracts the duplicated colorMap from HomePage, SubjectsPage, and ProgressPage
 * into a canonical utility. All module color lookups should use this.
 */

export interface ModuleColorSet {
  /** Solid background (e.g. bg-blue-500) */
  bg: string
  /** Gradient start (e.g. from-blue-500) */
  from: string
  /** Gradient end (e.g. to-cyan-500) */
  to: string
  /** Text color (e.g. text-blue-600) */
  text: string
  /** Border color (e.g. border-blue-200) */
  border: string
  /** Hex color for SVG strokes */
  ring: string
  /** Light background (e.g. bg-blue-50) */
  light: string
  /**
   * Per-wereld tint — een zeer subtiele hex-wash (≈4-6% van de module-hue
   * op een warm-off-white #FAFAF8 base). Bedoeld als achtergrond voor de
   * practice-ervaring: kaarten en tekst blijven leesbaar, maar het kind
   * voelt "ik ben nu in de Reken-wereld" / "ik ben in de Topo-wereld".
   * Zie APP_FEEL_2027_UPGRADE.md §3.1.
   */
  tint: string
}

const MODULE_COLOR_MAP: Record<string, ModuleColorSet> = {
  'text-blue-500': {
    bg: 'bg-blue-500',
    from: 'from-blue-500',
    to: 'to-cyan-500',
    text: 'text-blue-600',
    border: 'border-blue-200',
    ring: '#3B82F6',
    light: 'bg-blue-50',
    tint: '#F0F4FB',
  },
  'text-indigo-500': {
    bg: 'bg-indigo-500',
    from: 'from-indigo-500',
    to: 'to-purple-500',
    text: 'text-indigo-600',
    border: 'border-indigo-200',
    ring: '#6366F1',
    light: 'bg-indigo-50',
    tint: '#F2F2FB',
  },
  'text-emerald-500': {
    bg: 'bg-emerald-500',
    from: 'from-emerald-500',
    to: 'to-teal-500',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    ring: '#10B981',
    light: 'bg-emerald-50',
    tint: '#EDF7F1',
  },
  'text-rose-500': {
    bg: 'bg-rose-500',
    from: 'from-rose-500',
    to: 'to-pink-500',
    text: 'text-rose-600',
    border: 'border-rose-200',
    ring: '#F43F5E',
    light: 'bg-rose-50',
    tint: '#FBEEF0',
  },
  'text-amber-500': {
    bg: 'bg-amber-500',
    from: 'from-amber-500',
    to: 'to-orange-500',
    text: 'text-amber-600',
    border: 'border-amber-200',
    ring: '#F59E0B',
    light: 'bg-amber-50',
    tint: '#FAF3E4',
  },
  'text-purple-500': {
    bg: 'bg-purple-500',
    from: 'from-purple-500',
    to: 'to-fuchsia-500',
    text: 'text-purple-600',
    border: 'border-purple-200',
    ring: '#8B5CF6',
    light: 'bg-purple-50',
    tint: '#F4EFFB',
  },
  'text-teal-500': {
    bg: 'bg-teal-500',
    from: 'from-teal-500',
    to: 'to-cyan-500',
    text: 'text-teal-600',
    border: 'border-teal-200',
    ring: '#14B8A6',
    light: 'bg-teal-50',
    tint: '#EBF6F4',
  },
  'text-cyan-500': {
    bg: 'bg-cyan-500',
    from: 'from-cyan-500',
    to: 'to-blue-500',
    text: 'text-cyan-600',
    border: 'border-cyan-200',
    ring: '#06B6D4',
    light: 'bg-cyan-50',
    tint: '#E9F5F9',
  },
  'text-orange-500': {
    bg: 'bg-orange-500',
    from: 'from-orange-500',
    to: 'to-amber-500',
    text: 'text-orange-600',
    border: 'border-orange-200',
    ring: '#F97316',
    light: 'bg-orange-50',
    tint: '#FAF1E8',
  },
  // Fase 0: TGL Split — TIJD module (Clock, sky blue)
  'text-sky-500': {
    bg: 'bg-sky-500',
    from: 'from-sky-500',
    to: 'to-blue-500',
    text: 'text-sky-600',
    border: 'border-sky-200',
    ring: '#0EA5E9',
    light: 'bg-sky-50',
    tint: '#ECF4FA',
  },
  // Fase 0: TGL Split — GELD module (Wallet, gold/yellow — avoids green/red validation conflict)
  'text-yellow-500': {
    bg: 'bg-yellow-500',
    from: 'from-yellow-500',
    to: 'to-amber-500',
    text: 'text-yellow-600',
    border: 'border-yellow-200',
    ring: '#EAB308',
    light: 'bg-yellow-50',
    tint: '#FAF6E1',
  },
  // SpellingWW module (PenTool, fuchsia — avoids red validation conflict)
  'text-fuchsia-500': {
    bg: 'bg-fuchsia-500',
    from: 'from-fuchsia-500',
    to: 'to-pink-500',
    text: 'text-fuchsia-600',
    border: 'border-fuchsia-200',
    ring: '#D946EF',
    light: 'bg-fuchsia-50',
    tint: '#FAEEFC',
  },
}

/** Default fallback color set (indigo) */
const DEFAULT_COLORS: ModuleColorSet = MODULE_COLOR_MAP['text-indigo-500']

/**
 * Get the full color set for a module based on its color class string.
 *
 * @param colorClass - The module's color property (e.g. 'text-blue-500')
 * @returns Full color set with bg, gradient, text, border, ring, and light variants
 */
export function getModuleColors(colorClass: string): ModuleColorSet {
  return MODULE_COLOR_MAP[colorClass] || DEFAULT_COLORS
}

/**
 * Get the hex ring color for SVG strokes.
 *
 * @param colorClass - The module's color property (e.g. 'text-blue-500')
 * @returns Hex color string (e.g. '#3B82F6')
 */
export function getModuleHexColor(colorClass: string): string {
  return (MODULE_COLOR_MAP[colorClass] || DEFAULT_COLORS).ring
}

/**
 * Get the per-world tint hex for the practice background wash.
 * Returns a very subtle off-white-with-hue color that's safe to use as
 * a full-screen background without affecting card/text contrast.
 *
 * @param colorClass - The module's color property (e.g. 'text-blue-500')
 * @returns Hex tint color (e.g. '#F0F4FB')
 */
export function getModuleTint(colorClass: string): string {
  return (MODULE_COLOR_MAP[colorClass] || DEFAULT_COLORS).tint
}

/**
 * Lighten a 6-digit hex color toward white by `amount` (0..1).
 *
 * Used by per-world theming (APP_FEEL §3.1) to derive a depth-having
 * gradient from a single module hex: a top-stop with more color
 * (~10-15%) and a bottom-stop near-white (~3-5%) so the practice header
 * fades naturally into the neutral body instead of meeting it on a hard line.
 *
 * Falls back to the input hex if the format isn't `#RRGGBB`.
 */
export function lightenHex(hex: string, amount: number): string {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return hex
  const clamp = Math.max(0, Math.min(1, amount))
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const lr = Math.round(r + (255 - r) * clamp)
  const lg = Math.round(g + (255 - g) * clamp)
  const lb = Math.round(b + (255 - b) * clamp)
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${toHex(lr)}${toHex(lg)}${toHex(lb)}`
}

export { MODULE_COLOR_MAP }
